import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';

import { Button } from '../../components/button';
import Page from '../../components/page/Page';
import { OpenBookmarkRejectedError } from '../../utilities/bookmark';
import { profileSelectors } from '../../redux/selectors/profile';
import text from '../../text';
import { captureError } from '../../utilities/error';
import { useRedirect } from '../../hooks/useRedirect';
import useUrlParam from '../../mp/hooks/useUrlParam';
import {
  useCheckJobAccessQuery,
  useCheckProjectAccessQuery,
  useOpenBookmarkQuery,
  User,
  useRequestAccessMutation,
  UserProject,
} from '../../redux/rtk';
import useSearchParam from '../../hooks/useSearchParam';
import { format } from 'date-fns';

const RequestAccess = ({
  shareToken,
  projectId,
  jobId,
}: {
  shareToken?: string;
  projectId?: number;
  jobId?: number;
}) => {
  const redirect = useRedirect();
  const page = useSearchParam('page');

  const user = useSelector(profileSelectors.getUserProfile);
  const [requestAccess, requestAccessState] = useRequestAccessMutation();

  const checkJobAccessState = useCheckJobAccessQuery(
    { jobId: jobId! },
    { skip: !jobId }
  );
  const checkProjectAccessState = useCheckProjectAccessQuery(
    { projectId: projectId! },
    { skip: !projectId }
  );
  const openBookmarkState = useOpenBookmarkQuery(
    { shareToken: shareToken!, redirectToRequestAccess: false },
    { skip: !shareToken }
  );

  const [isRejected, setIsRejected] = useState(false); // was access denied?
  const [accessResult, setAccessResult] = useState<{
    requestor: User;
    created_at?: string;
  } | null>(null);

  const handleRequestAccessClicked = useCallback(async () => {
    await requestAccess({ shareToken, projectId, jobId });
  }, [requestAccess, shareToken, projectId, jobId]);

  useEffect(() => {
    const error = openBookmarkState.error;
    if (error) captureError(error);
  }, [openBookmarkState.error]);

  useEffect(() => {
    const data = openBookmarkState.data;
    if (data) {
      if (data.isRejected) {
        setIsRejected(true);
      } else if (data.redirectUrl) {
        redirect.push(data.redirectUrl);
      }
    }
  }, [openBookmarkState.data, redirect]);

  useEffect(() => {
    const err = openBookmarkState.error;
    if (!err) return;
    if (err instanceof OpenBookmarkRejectedError) {
      setIsRejected(true);
    } else {
      captureError(err);
    }
  }, [openBookmarkState.error]);

  const handleCheckAccess = useCallback(
    (
      userProject: UserProject & {
        invite_status_updater: User;
        created_at?: string;
      }
    ) => {
      setAccessResult({
        requestor: userProject.invite_status_updater,
        created_at: userProject.created_at,
      });
      if (userProject.invite_status === 'rejected') {
        setIsRejected(true);
      } else if (userProject.invite_status === 'accepted' && page) {
        redirect.push(page);
      }
    },
    [page, redirect]
  );

  useEffect(() => {
    if (checkJobAccessState.data) handleCheckAccess(checkJobAccessState.data);
  }, [checkJobAccessState.data, handleCheckAccess]);

  useEffect(() => {
    if (checkJobAccessState.error) captureError(checkJobAccessState.error);
  }, [checkJobAccessState.error]);

  useEffect(() => {
    if (checkProjectAccessState.data)
      handleCheckAccess(checkProjectAccessState.data);
  }, [checkProjectAccessState.data, handleCheckAccess, page, redirect]);

  useEffect(() => {
    if (checkProjectAccessState.error)
      captureError(checkProjectAccessState.error);
  }, [checkProjectAccessState.error]);

  return (
    // @ts-expect-error bad typing
    <Page title='Request Access' className='bg-white'>
      <div className='request-access__container'>
        <div className='request-access__text-content'>
          {isRejected ? (
            <>
              <h1>{text('accessDenied')}</h1>
              <p className='request-access__fail'>
                {text('accessDeniedDescriptionLine1', {
                  name: accessResult?.requestor?.full_name,
                  email: accessResult?.requestor?.email,
                })}
              </p>
              <p className='request-access__fail'>
                {text('accessDeniedDescriptionLine2')}
              </p>
              <p className='mt-4'>
                {text('you')} - {user.full_name} (
                <span className='font-weigth--500 font-green'>
                  {user.email}
                </span>
                ) - {text('requestedAccess')}{' '}
                {format(new Date(), 'd MMM yyyy, h:mm a')}
              </p>

              <img
                className='mt-2'
                src='/public/img/cart-empty-illustration-no-addons.png'
                alt='access denied'
              />
            </>
          ) : (
            <>
              {!requestAccessState.isLoading &&
              !requestAccessState.error &&
              requestAccessState.isSuccess ? (
                <>
                  <p className='request-access__success mb-lg'>
                    {text('accessRequestSent')}
                  </p>
                  <p className='font-16-important font-weight--600-important'>
                    {text('accessRequestEmail')}
                  </p>
                  <p>{text('accessRequestNote')}</p>
                  {user ? (
                    <>
                      <p className='mb-md'>{text('profileRequestingAccess')}</p>
                      <p>
                        {user.full_name}
                        <br />
                        {user.email}
                      </p>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <h1 className='mt-4'>{text('youNeedAccess')}</h1>
                  <p>{text('youNeedToBeInvited')}</p>
                  <Button
                    onClick={handleRequestAccessClicked}
                    loading={requestAccessState.isLoading}
                    className='request-access__button'
                  >
                    {text('requestAccess')}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
        {!isRejected && (
          <ReactSVG
            src='/public/img/request-access.svg'
            className='request-access__img'
          />
        )}
      </div>
    </Page>
  );
};

const RequestAccessShareLink = () => {
  const shareToken = useUrlParam('shareToken');
  return <RequestAccess shareToken={shareToken} />;
};

const RequestAccessProject = () => {
  const projectId = useUrlParam('projectId', { int: true });
  return <RequestAccess projectId={projectId} />;
};

const RequestAccessJob = () => {
  const jobId = useUrlParam('jobId', { int: true });
  return <RequestAccess jobId={jobId} />;
};

const RequestAccessPage = {
  ShareLink: RequestAccessShareLink,
  Project: RequestAccessProject,
  Job: RequestAccessJob,
};

export default RequestAccessPage;
